import { withTheme } from "@hoc/withTheme";
import { WithOData, withOData } from "@odata/withOData";
import { transparentize } from "polished";
import React, { memo, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { PermissionContext } from "../../../contexts/permissionContext/PermissionContext";
import { PropsWithTheme } from "../../../theme";
import { IDashboardTileComponentProps, ITableTileData, ITableTileInfo } from "../../dashboard";
import DashboardTileBackground from "../../dashboard/DashboardTileBackground";
import DashboardTileHeader from "../../dashboard/DashboardTileHeader";
import { getInfoValue } from "../../smart/FieldInfo";
import { ICustomTableColors } from "../../table";
import TableWithAutoSizedColumns from "../../table/TableWithAutoSizedColumns";
import { useDashboardTile } from "../hooks/useDashboardTile";
import { TableTileTableWrapper } from "./TableTile.styles";

interface IProps extends IDashboardTileComponentProps<ITableTileInfo>, WithOData, PropsWithTheme {
}

const TableTile: React.FC<IProps> = (props) => {
    const context = useContext(AppContext);
    const permissionContext = useContext(PermissionContext);
    const { data, isLoading, hasError } = useDashboardTile<ITableTileData>({
        getTileDataArgs: {
            prop: props.info.tableData,
            args: { context, permissionContext, oData: props.oData }
        }
    });

    const { t } = useTranslation("Home");

    const customTableColors = useMemo((): ICustomTableColors => {
        return {
            rowBackgroundColor: transparentize(0.7, props.theme.C_ACT_hover_without_opacity),
            rowBorderColor: props.theme.C_BG_menu
        };
    }, [props.theme.C_ACT_hover_without_opacity, props.theme.C_BG_menu]);

    const isWithoutHeader = data?.columns && !data.columns.find(col => col.label);

    const to = getInfoValue(props.info, "link", { context });

    return (
            <DashboardTileBackground backgroundColor={props.info.backgroundColor}
                                     inEditMode={props.inEditMode}
                                     isLoading={isLoading || hasError/*todo: may be different look for error state*/}
                                     to={to}>
                <DashboardTileHeader tooltip={props.info.tooltip}>{props.info.title}</DashboardTileHeader>
                <TableTileTableWrapper $isWithoutHeader={isWithoutHeader}>
                    <TableWithAutoSizedColumns
                            columns={data?.columns ?? []}
                            rows={data?.rows ?? []}
                            noDataText={props.info.noDataText ?? t("NoData")}
                            hasSimplifiedNoData
                            customColors={customTableColors}
                            disableSort
                            disableVirtualization
                            disableColumnResize
                            isHoverDisabled
                            useStatusHighlight={false}
                            withoutShadows
                            assignRemainingSpaceToFirstColumn
                    />
                </TableTileTableWrapper>
            </DashboardTileBackground>
    );
};

export default memo(withTheme(withOData(TableTile)));